import { useSessionTopicBox } from "./use-session-topic-box";
import { useCallback, useMemo, type ReactNode } from "react";

import { Bento } from "@/components/ui/box";
import type { Box, Read } from "@/components/ui/box.schema";
import type { SessionWorkspace } from "@/lib/api/sessionPortal";

import type { SessionPlanParts } from "../ClassPlanCard";
import { usePrepareBox } from "../SessionPrepare";
import { rankOf } from "./session-ranking";
import { useCloseBox } from "./use-close-box";
import { useFollowUpBox } from "./use-follow-up-box";
import { useSessionReviewBoxes } from "./use-session-review-boxes";
import { reviewWork } from "../review/review-plan";
import { useAfterClassBox, useGetReadyBox } from "./use-learner-boxes";
import { useTeachBox } from "./use-teach-box";
import { useRunningOrderBox, useSessionCourseworkBox, useSessionFolderBox, useTeachingNotesBox } from "./use-session-plan-boxes";
import { useClassOverviewBox } from "./use-class-overview-box";

/**
 * The teaching session page below the band, in one stable order (A2): what
 * the last session left, the session's identity, Prepare (what learners see),
 * the Running order, this session's Coursework, the private Teaching notes
 * and the optional Session folder, then where it meets and its attendance
 * and recap. One page with one lead (P1, P2, P4): each box's rank is the
 * server's ranking read; what the last session left ranks before this
 * session's own work.
 */
const ignore = () => undefined;

export function TeachingSessionBento({
  workspace,
  read,
  parts,
  allSessionsPath,
  followQuiet = false,
  teachBody,
  teachChanges = false,
  reviewShown = false,
  closeRanks = false,
  onReviewOwnsAct,
  after,
  outlinePath,
}: {
  workspace: SessionWorkspace;
  read: Read<SessionWorkspace>;
  parts: SessionPlanParts;
  /** Follow up's way to the course's sessions, when more earlier sessions wait than it lists. */
  allSessionsPath: string;
  /** Another box holds the act the person opened by hand, so Follow up offers none. */
  followQuiet?: boolean;
  /** Where it meets, and its editor for the teaching team. */
  teachBody?: ReactNode;
  /** Whether Teach's body offers a change, so its exit says Change rather than where it meets. */
  teachChanges?: boolean;
  /** Whether this session has work to review: each piece is a Review box after Close. */
  reviewShown?: boolean;
  /** Close holds the page's act, so Review opens nothing by itself. */
  closeRanks?: boolean;
  /** An attempt opened by hand in Review owns the page's act. */
  onReviewOwnsAct?: (owns: boolean) => void;
  /** Boxes not yet on the kit, drawn after the page until their areas move them. */
  after?: ReactNode;
  /** The course's Desk, whose Outline places this class in its topic. */
  outlinePath?: string;
}) {
  const { session, course } = workspace;
  const overview = useClassOverviewBox({ workspace, parts, outlinePath: outlinePath ?? allSessionsPath });
  const topic = useSessionTopicBox(course.id, session.id, true);
  const followUp = useFollowUpBox({ read, quiet: followQuiet, allSessionsPath });
  const prepare = usePrepareBox({ workspace, parts, rank: rankOf(read, "prepare") });
  const runningOrder = useRunningOrderBox({ workspace, parts });
  const reviewHere = useMemo(() => new Set(reviewShown ? reviewWork(workspace).map((work) => work.id) : []), [reviewShown, workspace]);
  const coursework = useSessionCourseworkBox({ workspace, reviewHere });
  const notes = useTeachingNotesBox({ workspace, parts });
  const folder = useSessionFolderBox({ workspace });
  const teach = useTeachBox({ workspace, read, teaching: true, rank: rankOf(read, "teach"), body: teachBody, changes: teachChanges });
  const close = useCloseBox({ workspace, parts, rank: rankOf(read, "close") });
  const review = useSessionReviewBoxes({ workspace, shown: reviewShown, closeRanks, onOwnsAct: onReviewOwnsAct ?? ignore });
  const boxes = [followUp.box, overview, topic, prepare.box, runningOrder, coursework, notes, folder, teach, close, ...review.boxes].filter(Boolean) as Box[];
  const followRowOpen = followUp.onRowOpenChange, reviewRowOpen = review.onRowOpenChange;
  const onRowOpenChange = useCallback((box: string, row: string, open: boolean) => {
    followRowOpen(box, row, open);
    reviewRowOpen(box, row, open);
  }, [followRowOpen, reviewRowOpen]);
  const prepareOpen = prepare.onOpenChange;
  const onOpenChange = (box: string, open: boolean) => { if (box === "prepare") prepareOpen(open); };
  return (
    <>
      <Bento id="session" journey="session-teaching" zone={course.timezone} boxes={boxes} onOpenChange={onOpenChange} onRowOpenChange={onRowOpenChange} />
      {followUp.hosts}
      {review.hosts}
      {after}
    </>
  );
}

/**
 * The learning session page below the band: Get ready (or Does not meet),
 * then After class, in time order (P4). Join leads only when the server ranks
 * Get ready first. Hand in is A3's and is drawn after the page until A3's boxes join this
 * Bento between the two (listed deviation).
 */
export function LearningSessionBento({ read, where, onRetry, allSessionsPath, after }: {
  read: Read<SessionWorkspace>;
  allSessionsPath: string;
  /** Where it meets, read only. */
  where: ReactNode;
  onRetry: () => void;
  after?: ReactNode;
}) {
  const followUp = useFollowUpBox({ read, quiet: false, allSessionsPath });
  const workspace = read.status === "ready" ? read.value : read.status === "stale" ? read.last : undefined;
  const topic = useSessionTopicBox(workspace?.course.id ?? "", workspace?.session.id ?? "", false);
  const ready = useGetReadyBox({ read, rank: rankOf(read, "get-ready"), where, onRetry });
  const afterClass = useAfterClassBox({ read });
  const zone = read.status === "ready" ? read.value.course.timezone : read.status === "stale" ? read.last.course.timezone : undefined;
  return (
    <>
      <Bento id="session" journey="session-learning" zone={zone} boxes={[followUp.box, topic, ready, afterClass].filter(Boolean) as Box[]} />
      {after}
    </>
  );
}

/** One session's Prepare or Close on its own, for a host that opens just that job. */
export function SessionTaskBento({ kind, workspace, parts }: {
  kind: "prepare" | "close";
  workspace: SessionWorkspace;
  parts: SessionPlanParts;
}) {
  const prepare = usePrepareBox({ workspace, parts, rank: null });
  const close = useCloseBox({ workspace, parts, rank: null });
  const topic = useSessionTopicBox(workspace.course.id, workspace.session.id, true, kind === "prepare");
  const box = kind === "prepare" ? prepare.box : close;
  const onOpenChange = (id: string, open: boolean) => { if (id === "prepare") prepare.onOpenChange(open); };
  return box ? <Bento id={`session-${kind}`} journey={`session-${kind}`} zone={workspace.course.timezone} boxes={[topic, box].filter(Boolean) as Box[]} defaultOpen={[box.id]} onOpenChange={onOpenChange} /> : null;
}
